import { useEffect, useRef, type ReactElement } from 'react';
import { Outlet, ScrollRestoration, useLocation } from 'react-router-dom';

import { AndroidAppBanner } from '@/components/layout/AndroidAppBanner.js';
import { IosAppBanner } from '@/components/layout/IosAppBanner.js';
import { useCatalog } from '@/lib/i18n/catalog-context.js';

import { ROUTES } from './routes.js';

/**
 * Общая оболочка маршрутов.
 *
 * Две вещи, которые в SPA не работают сами и без которых Lighthouse справедливо ругается:
 *
 * 1. ScrollRestoration — браузер восстанавливает позицию прокрутки только при настоящей
 *    навигации. При клиентской новая страница открывалась бы прокрученной вниз.
 *
 * 2. Перевод фокуса. При смене документа браузер сбрасывает фокус на начало; при клиентской
 *    навигации фокус остаётся на нажатой ссылке, и пользователь клавиатуры или скринридера
 *    после перехода продолжает с прежнего места, не узнав, что страница сменилась.
 *    Поэтому фокус переводится на контейнер контента — но НЕ на первом рендере, иначе
 *    мы бы отобрали фокус у пользователя сразу после загрузки.
 *
 * 3. Skip-link. Волна 07: у страниц появилась шапка с навигацией, и без него пользователь
 *    клавиатуры обходит её заново на каждом переходе. Ссылка видима только в фокусе;
 *    цель — id="main", который выставляют оболочки страниц.
 *
 * 4. Предложение установить приложение (iPhone — App Store, Android — Google Play). Здесь потому, что это единственная
 *    общая оболочка всех маршрутов: смонтировать его на каждой странице отдельно значило
 *    бы завести места, где о нём забудут.
 */
export function RootLayout(): ReactElement {
  const c = useCatalog();
  const location = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    contentRef.current?.focus();
  }, [location.pathname]);

  /**
   * ⚠️ Админка исключена, и это не косметика: её маршруты — дети ЭТОГО же RootLayout
   * (app/router.tsx), а админской части в приложении не существует. Предложение
   * установить приложение на экране кассы вело бы туда, где кассы нет.
   *
   * Проверка по пути, а не по каркасу, потому что каркас здесь один на всех. В соседнем
   * проекте кампуса то же исключение получилось структурно — там у админки свой layout.
   */
  const isAdmin = location.pathname.startsWith(ROUTES.adminSignin);

  return (
    <>
      <ScrollRestoration />
      <a
        href="#main"
        className="sr-only rounded-md bg-mm-ink px-4 py-2 text-white focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50"
      >
        {c.common.skipToContent}
      </a>
      {isAdmin ? null : (
        <>
          <IosAppBanner />
          <AndroidAppBanner />
        </>
      )}
      {/* tabIndex={-1} делает контейнер фокусируемым программно, но не с клавиатуры:
          лишней остановки в порядке обхода Tab не появляется. */}
      <div ref={contentRef} tabIndex={-1} className="outline-none">
        <Outlet />
      </div>
    </>
  );
}
